import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Label, Modal, ModalActions, ModalContent, ModalTitle, Toggle } from '@c2fo/liquidity';
import useUpdateIncludeNewBuyer from '@/enterprise/data/useUpdateIncludeNewBuyer';
import { useReporting } from '@/reporting';
import globalRotateSvg from '@/shared/assets/globalRotate.svg?url';
import FullPageLoader from '@/shared/components/FullPageLoader';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useIncludeNewBuyerState from '@/shared/data/useIncludeNewBuyerState';
import CardForm from '../components/CardForm';

const PreferencesComponent = () => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { isPending, mutateAsync } = useUpdateIncludeNewBuyer();
  const { isEnrolled } = useIncludeNewBuyerState();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleToggleChange = async (checked: boolean) => {
    if (isEnrolled && !checked) {
      setShowCancelModal(true);
      return;
    }

    await mutateAsync(checked);

    trackEnterpriseEvent('auto-setup-offers-for-new-customers::submit', {
      referrer: 'account-preferences',
      toggleValue: checked,
    });
  };

  const handleCancelEnrollment = async () => {
    await mutateAsync(false);
    setShowCancelModal(false);

    trackEnterpriseEvent('auto-setup-offers-for-new-customers::submit', {
      referrer: 'account-preferences',
      toggleValue: false,
    });
  };

  const handleKeepEnrollment = () => {
    setShowCancelModal(false);
  };

  return (
    <QueryBoundaries>
      <div className="space-y-9">
        <CardForm.Card
          title={t('includeNewBuyer.title')}
          content={
            <CardForm.CardContent className="flex flex-col gap-6 md:flex-row md:items-center">
              <div className="shrink-0">
                <img src={globalRotateSvg} alt={t('includeNewBuyer.title')} className="size-[72px]" />
              </div>
              <div>
                <div className="w-[80%]">{t('includeNewBuyer.longDescription')}</div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <Label htmlFor="includeNewBuyer">{isEnrolled ? t('core.enrolled') : t('core.notEnrolled')}</Label>
                <Toggle
                  id="includeNewBuyer"
                  name="includeNewBuyer"
                  checked={isEnrolled}
                  className="shrink-0"
                  disabled={isPending}
                  loading={isPending}
                  onCheckedChange={handleToggleChange}
                />
              </div>
            </CardForm.CardContent>
          }
        />
      </div>
      <Modal onClose={handleKeepEnrollment} open={showCancelModal} size="sm">
        <ModalTitle>{t('includeNewBuyer.cancelEnrollmentTitle')}</ModalTitle>
        <ModalContent>
          <div>{t('includeNewBuyer.cancelEnrollmentDescription1')}</div>
          <div>{t('includeNewBuyer.cancelEnrollmentDescription2')}</div>
        </ModalContent>
        <ModalActions>
          <Button onClick={handleKeepEnrollment} variant="secondary">
            {t('includeNewBuyer.keepEnrollment')}
          </Button>
          <Button onClick={handleCancelEnrollment} variant="primary" loading={isPending}>
            {t('includeNewBuyer.cancelEnrollment')}
          </Button>
        </ModalActions>
      </Modal>
    </QueryBoundaries>
  );
};

const Preferences = () => (
  <QueryBoundaries
    LoadingComponent={() => (
      <div className="-mt-36">
        <FullPageLoader />
      </div>
    )}
  >
    <PreferencesComponent />
  </QueryBoundaries>
);

export default Preferences;
